import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../Config';

// Composant d'alerte minimaliste
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-500' : 
                  type === 'error' ? 'bg-rose-500' : 
                  'bg-blue-500';

  return (
    <div className={`fixed top-6 right-6 z-50 px-4 py-3 ${bgColor} text-white rounded-lg shadow-lg transform transition-transform duration-300 animate-slide-in`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {type === 'success' && (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {type === 'error' && (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="font-medium">{message}</span>
        </div>
        <button 
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200 focus:outline-none"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Modale de confirmation simplifiée
const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-2 text-gray-600 text-sm">{message}</p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors"
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [form, setForm] = useState({ nom: '', description: '' });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirm, setConfirm] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    onConfirm: null 
  });
  
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirm({ show: true, title, message, onConfirm });
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  const hideConfirm = () => {
    setConfirm({ ...confirm, show: false });
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/categories`, { headers });
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          navigate('/login');
          return;
        }
        throw new Error('Erreur chargement');
      }

      const data = await response.json();
      setCategories(data.data || []);
    } catch (err) {
      console.error('Erreur:', err);
      showToast('Erreur lors du chargement des catégories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCat) {
        const response = await fetch(`${API_URL}/categories/${editingCat.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(form)
        });

        if (!response.ok) throw new Error('Erreur mise à jour');
        showToast('Catégorie mise à jour avec succès');
      } else {
        const response = await fetch(`${API_URL}/categories`, {
          method: 'POST',
          headers,
          body: JSON.stringify(form)
        });

        if (!response.ok) throw new Error('Erreur création');
        showToast('Catégorie créée avec succès');
      }

      setForm({ nom: '', description: '' });
      setShowForm(false);
      setEditingCat(null);
      fetchCategories();
    } catch (err) {
      showToast('Erreur lors de l\'opération', 'error');
      console.error('Erreur:', err);
    }
  };

  const handleEdit = (cat) => {
    setEditingCat(cat);
    setForm({ nom: cat.nom, description: cat.description || '' });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (cat) => {
    showConfirm(
      'Supprimer la catégorie',
      `Êtes-vous sûr de vouloir supprimer "${cat.nom}" ?`,
      async () => {
        try {
          const response = await fetch(`${API_URL}/categories/${cat.id}`, {
            method: 'DELETE',
            headers
          });

          if (!response.ok) throw new Error('Erreur suppression');
          
          showToast('Catégorie supprimée avec succès');
          fetchCategories();
        } catch (err) {
          showToast('Impossible de supprimer cette catégorie', 'error');
          console.error('Erreur:', err);
        } finally {
          hideConfirm();
        }
      }
    );
  };

  const toggleStatus = async (cat) => {
    const action = cat.actif ? 'désactiver' : 'activer';
    showConfirm(
      `${cat.actif ? 'Désactiver' : 'Activer'} la catégorie`,
      `Voulez-vous ${action} "${cat.nom}" ?`,
      async () => {
        try {
          const response = await fetch(`${API_URL}/categories/${cat.id}/toggle-status`, {
            method: 'PUT',
            headers
          });

          if (!response.ok) throw new Error('Erreur statut');
          showToast(`Catégorie ${cat.actif ? 'désactivée' : 'activée'} avec succès`);
          fetchCategories();
        } catch (err) {
          console.error('Erreur:', err);
          showToast('Erreur lors du changement de statut', 'error');
        } finally {
          hideConfirm();
        }
      }
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast}
        />
      )}

      {/* Confirmation Dialog */}
      {confirm.show && (
        <ConfirmDialog
          isOpen={confirm.show}
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={hideConfirm}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Catégories</h1>
              <p className="text-gray-500 mt-1">Gestion des catégories de matériel</p>
            </div>
            <button
              onClick={() => { 
                setShowForm(true); 
                setEditingCat(null); 
                setForm({ nom: '', description: '' });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Nouvelle catégorie
            </button>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-gray-900">{categories.length}</div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-emerald-600">
                {categories.filter(c => c.actif).length}
              </div>
              <div className="text-sm text-gray-500">Actives</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-amber-600">
                {categories.filter(c => !c.actif).length}
              </div>
              <div className="text-sm text-gray-500">Inactives</div>
            </div>
          </div>
        </div>

        {/* Formulaire */}
        {showForm && (
          <div className="mb-8 bg-white rounded-xl border p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingCat ? 'Modifier' : 'Nouvelle catégorie'}
              </h2>
              <button
                onClick={() => { setShowForm(false); setEditingCat(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  placeholder="Nom de la catégorie"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  placeholder="Description (optionnel)"
                  rows="3"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button 
                  type="submit" 
                  className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  {editingCat ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingCat(null); }}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Catégorie</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Description</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Matériels</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Statut</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="text-gray-400 mb-3">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <p className="text-gray-500 mb-4">Aucune catégorie disponible</p>
                      <button
                        onClick={() => setShowForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                      >
                        Créer la première
                      </button>
                    </td>
                  </tr>
                ) : (
                  categories.map((cat) => (
                    <tr key={cat.id} className="hover:">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{cat.nom}</div>
                        <div className="text-xs text-gray-500 mt-0.5">ID: {cat.id}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600 max-w-xs">
                          {cat.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                          {cat.materiels_count || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                          cat.actif 
                            ? 'bg-emerald-100 text-emerald-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {cat.actif ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(cat)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 hover:bg-blue-50 rounded"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => toggleStatus(cat)}
                            className={`text-sm font-medium px-3 py-1 rounded ${
                              cat.actif 
                                ? 'text-amber-600 hover:text-amber-800 hover:bg-amber-50' 
                                : 'text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50'
                            }`}
                          >
                            {cat.actif ? 'Désactiver' : 'Activer'}
                          </button>
                          <button
                            onClick={() => handleDelete(cat)}
                            disabled={cat.materiels_count > 0}
                            className={`text-sm font-medium px-3 py-1 rounded ${
                              cat.materiels_count > 0 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'text-rose-600 hover:text-rose-800 hover:bg-rose-50'
                            }`}
                            title={cat.materiels_count > 0 ? "Catégorie utilisée" : ""}
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {categories.length > 0 && (
            <div className="px-6 py-4 border-t">
              <div className="flex justify-between items-center text-sm text-gray-600">
                <div>
                  {categories.length} catégorie{categories.length > 1 ? 's' : ''}
                </div>
                <div>
                  Total matériels: {categories.reduce((sum, cat) => sum + (cat.materiels_count || 0), 0)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}